import { useDispatch, useSelector } from 'react-redux';
import { Snackbar, styled } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Switch } from 'antd';
import MuiAlert from '@material-ui/lab/Alert';
import InnerHeader from '../../component/InnerHeader';
import editIcon from '../../asset/edit.svg';
import checkIcon from '../../asset/check.svg';
import Copy from '../../component/Copy';
import { updateProjectAction } from '../../actions/ProjectAction';
import {
  getAllProvidersAction,
  toggleProviderActivationAction
} from '../../actions/ProviderActions';
import formatDate from '../../helpers/formatDate';
import NoxTable from '../../component/Table';

const Wrapper = styled('div')({
  padding: '1.5rem'
});

const ProjectDetailsWrapper = styled('div')({
  border: '1px solid #ccc',
  borderRadius: '0.4rem',
  padding: '0.5rem'
});

const ContentInner = styled('div')({
  '&:last-child': {
    paddingBottom: '0'
  },
  display: 'flex',
  justifyContent: 'flex-start',
  paddingBottom: '1rem'
});

const Heading = styled('div')({
  minWidth: '8rem',
  textAlign: 'start'
});

const Detail = styled('div')({
  fontSize: '0.875rem'
});

const CopyContainer = styled('div')({
  minWidth: '27rem'
});

const InputStyled = styled('input')({
  '&:focus': {
    outline: 'none'
  },
  background: '#FFFFFF',
  border: '1px solid #CAD8FF',
  borderRadius: '0.4rem',
  boxSizing: 'border-box'
});

const TableWrapper = styled('div')({
  marginTop: '2rem',
  paddingBottom: '2rem'
});

const IconStyled = styled('img')({
  marginLeft: '0.5rem'
});

const transformData = (providers) => {
  if (!providers) {
    return [];
  }
  return providers.map(({ _id, name, updated, isActive }, idx) => {
    const date = formatDate(updated);
    return {
      id: _id,
      isActive,
      key: idx,
      name,
      updated: date.toString() === 'Inv' ? '-' : date
    };
  });
};

const ProjectSettings = () => {
  const dispatch = useDispatch();
  const currentProject = useSelector(({ project }) => project.currentProject);
  const [
    providers,
    providersLoading,
    providersError,
    toggleProviderLoading,
    toggleProviderError
  ] = useSelector(({ providers }) => [
    providers.providersState.data,
    providers.providersState.isLoading,
    providers.providersState.error,
    providers.providerToggleState.isLoading,
    providers.providerToggleState.error
  ]);

  useEffect(() => {
    if (currentProject) {
      dispatch(getAllProvidersAction());
    }
  }, [dispatch, currentProject]);

  const [editMode, setEditMode] = useState(false);
  const [projectName, setProjectName] = useState(currentProject.name);

  const handleClickSave = () => {
    if (projectName && currentProject.name !== projectName) {
      currentProject.name = projectName;
      dispatch(updateProjectAction(currentProject));
    }
    setEditMode(false);
  };

  const toggleStatus = useCallback(
    (item) => {
      dispatch(toggleProviderActivationAction(currentProject, item));
    },
    [currentProject, dispatch]
  );

  const data = useMemo(() => transformData(providers), [providers]);

  const columns = useMemo(
    () => [
      {
        dataIndex: 'name',
        key: 'name',
        render: (name) => {
          return <Copy text={name} />;
        },
        title: 'Name'
      },
      /*    {
          title: 'Updated',
          dataIndex: 'updated',
          key: 'updated',
          render: (updated) => {
            return <Copy text={updated}/>;
          },
        }, */
      {
        key: 'isActive',
        render: (item) => (
          <Switch
            checked={item.isActive}
            loading={toggleProviderLoading}
            onChange={() => toggleStatus(item)}
          />
        ),
        title: 'Activate/Deactivate'
      }
    ],
    [toggleProviderLoading, toggleStatus]
  );

  return (
    <Wrapper>
      <InnerHeader title="Settings" />
      <ProjectDetailsWrapper>
        <ContentInner>
          <Heading>Project Name:</Heading>
          {!editMode ? (
            <div>
              <span style={{ fontSize: '0.875rem' }}>{currentProject.name}</span>
              <IconStyled alt="edit" onClick={() => setEditMode(true)} src={editIcon} />
            </div>
          ) : (
            <div>
              <InputStyled
                defaultValue={currentProject.name}
                onChange={(e) => setProjectName(e.target.value)}
                value={projectName}
              />
              <IconStyled alt="edit" onClick={handleClickSave} src={checkIcon} />
            </div>
          )}
        </ContentInner>
        <ContentInner>
          <Heading>Number of Users:</Heading>
          <Detail>{currentProject.users.length}</Detail>
        </ContentInner>
        <ContentInner>
          <Heading>API Key:</Heading>
          <CopyContainer>
            <Copy text={currentProject.api_key} />
          </CopyContainer>
        </ContentInner>
        <ContentInner>
          <Heading>Private API Key:</Heading>
          {currentProject.private_api_key ? (
            <CopyContainer>
              <Copy text={currentProject.private_api_key} />
            </CopyContainer>
          ) : null}
        </ContentInner>
        <ContentInner>
          <Heading>Created Date:</Heading>
          <Detail>{formatDate(currentProject.created)}</Detail>
        </ContentInner>
      </ProjectDetailsWrapper>
      <TableWrapper>
        <NoxTable
          checkbox={false}
          columns={columns}
          data={data}
          increasedPagination
          loading={providersLoading}
          searchBy="name"
        />
      </TableWrapper>
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
        autoHideDuration={6000}
        open={providersError || toggleProviderError}
      >
        <MuiAlert elevation={6} severity="error" variant="filled">
          error... please try again!
        </MuiAlert>
      </Snackbar>
    </Wrapper>
  );
};

export default ProjectSettings;
